// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState } from "react";
import Header from "../../component/_elements/Header.tsx";
import { useOutletContext } from "react-router-dom";
import { OutletContextType, PonctualiteType } from "../../types/GlobalType.ts";
import { depenseContext } from "../../context/AppContext.js";

import ColumnChart from "../../component/boxes/chart/ChartColumn.tsx";
import CircularChartPaimentPerClass from "../../component/boxes/chart/CircularChartPaimentPerClass.tsx";
import { useQuery } from "@tanstack/react-query";
import { studentControler } from "../../_api/controllers/student.controller.ts";
import { personalController } from "../../_api/controllers/personal.controller.ts";
import { paymentController } from "../../_api/controllers/payment.controller.ts";
import { depenseController } from "../../_api/controllers/depense.controller.ts";
import { studentAttendanceController } from "../../_api/controllers/attendance.controller.ts";
import moment from "moment";
import { parentController } from "../../_api/controllers/parent.controller.ts";
import {
  classeController,
  motifPaiementController,
  promotionController,
  schoolController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { userController } from "../../_api/controllers/user.controller.ts";
import {
  usePermissions,
  useRole,
  useRolePermissions,
} from "../../_api/useAuth.js";
// import HasRole from "../../component/_elements/HasRole.tsx";

type MiniChartProps = {
  color?: string;
  variant?: 1 | 2 | 3 | 4;
};

const MiniChart = ({ color = "#94A3B8", variant = 1 }: MiniChartProps) => {
  const paths = {
    1: "M2 31 C10 25, 14 17, 22 19 C30 21, 32 29, 40 25 C48 21, 51 7, 59 8 C68 9, 70 21, 79 18 C88 15, 91 11, 99 12 C108 13, 111 19, 120 10",

    2: "M2 29 C10 24, 16 16, 24 19 C32 22, 36 29, 44 23 C52 17, 56 7, 64 10 C72 13, 74 22, 83 18 C91 14, 95 7, 103 12 C111 17, 114 16, 120 8",

    3: "M2 27 C9 23, 15 25, 21 19 C27 13, 31 15, 37 22 C43 29, 49 27, 55 18 C61 9, 68 11, 74 17 C80 23, 86 21, 92 14 C98 7, 105 12, 120 8",

    4: "M2 30 C10 27, 15 19, 23 21 C31 23, 35 15, 42 18 C49 21, 54 28, 62 22 C70 16, 75 9, 83 12 C91 15, 96 23, 103 18 C110 13, 115 10, 120 6",
  };

  const gradientId = `miniChartGradient-${variant}`;

  return (
    <svg
      viewBox="0 0 122 40"
      className="w-[115px] xl:w-[125px] h-[42px]"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.30" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* zone remplie */}
      <path
        d={`${paths[variant]} L120 40 L2 40 Z`}
        fill={`url(#${gradientId})`}
      />

      {/* courbe */}
      <path
        d={paths[variant]}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const DashboardHeaderSkeleton = ({ isDarkMode }: { isDarkMode: boolean }) => {
  return (
    <div className="mb-8 animate-pulse">
      {/* Title skeleton */}
      <div className="h-7 w-56 mb-2 rounded bg-gray-300 dark:bg-gray-700"></div>

      {/* Subtitle skeleton */}
      <div className="h-4 w-56 lg:w-[420px] rounded bg-gray-300 dark:bg-gray-700"></div>
    </div>
  );
};

// Skeleton for StatCard
const StatCardSkeleton = ({ isDarkMode }: { isDarkMode: boolean }) => {
  return (
    <div
      className={`rounded-lg p-6 animate-pulse ${
        isDarkMode ? "bg-[#161B22]" : "bg-white shadow-md"
      }`}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="h-4 w-24 md:w-32 rounded bg-gray-300 dark:bg-gray-700"></div>
        <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>

      {/* Main number */}
      <div className="mb-2">
        <div className="h-8 w-20 rounded bg-gray-300 dark:bg-gray-700"></div>
      </div>

      {/* Description */}
      <div className="h-3 w-full rounded bg-gray-300 dark:bg-gray-700"></div>
    </div>
  );
};

// Skeleton for PaymentStats
const PaymentStatsSkeleton = ({ isDarkMode }: { isDarkMode: boolean }) => {
  return (
    <div
      className={`rounded-lg p-6 lg:col-span-2 ${
        isDarkMode ? "bg-[#161B22]" : "bg-white shadow-md"
      } animate-pulse`}
    >
      {/* Title skeleton */}
      {/* <div className="h-6 w-72 mb-6 rounded bg-gray-300 dark:bg-gray-700"></div> */}

      {/* Chart skeleton */}
      <div className="space-y-4">
        {/* Fake bars */}
        <div className="flex items-end gap-3 h-48">
          {[...Array(12)].map((_, index) => (
            <div
              key={index}
              className="flex-1 rounded bg-gray-300 dark:bg-gray-700"
              style={{ height: `${60 + Math.random() * 40}%` }}
            ></div>
          ))}
        </div>

        {/* X-axis labels */}
        {/* <div className="flex justify-between mt-4">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="h-3 w-10 rounded bg-gray-300 dark:bg-gray-700"
            ></div>
          ))}
        </div> */}
      </div>
    </div>
  );
};

// Skeleton for StudentStats
const StudentStatsSkeleton = ({ isDarkMode }: { isDarkMode: boolean }) => {
  return (
    <div
      className={`rounded-lg p-6 ${
        isDarkMode ? "bg-[#161B22]" : "bg-white shadow-md"
      } animate-pulse`}
    >
      {/* Card title */}
      <div className="h-6 w-64 mb-6 rounded bg-gray-300 dark:bg-gray-700"></div>

      {/* Inner box */}
      <div
        className={`rounded-lg p-4 ${
          isDarkMode ? "bg-[#0D1117]" : "bg-gray-50"
        }`}
      >
        {/* Section title */}
        <div className="h-4 w-28 mb-4 rounded bg-gray-300 dark:bg-gray-700"></div>

        {/* Circular chart skeleton */}
        <div className="flex justify-center">
          <div className="w-full h-40 rounded bg-gray-300 dark:bg-gray-700"></div>
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [currentYear, setCurrentYear] = useState("2025 - 2026");
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  const toggleYearDropdown = () => {
    setIsYearDropdownOpen(!isYearDropdownOpen);
  };

  //fetch anne scolaire
  useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  //fetch school identity
  useQuery({
    queryKey: ["school"],
    queryFn: schoolController.getAll,
  });

  //get role and permission
  const {
    data: roleRoleData,
    // isPending: isRoleLoading,
    isSuccess: isRoleSuccess,
  } = useRole();
  const {
    data: rolePermissionData,
    // isPending: isRolePermissionLoading,
    isSuccess: isRolePermissionSuccess,
  } = useRolePermissions();
  const {
    data: PermissionData,
    // isPending: isPermissionLoading,
    isSuccess: isPermissionSuccess,
  } = usePermissions();

  const roleRoleDataArr = roleRoleData?.data || [];
  const rolePermissionDataArr = rolePermissionData?.data || [];
  const PermissionDataArr = PermissionData?.data || [];

  //updateUser role and permission
  //get user data from localstorage
  const userDataString = localStorage.getItem("user");
  const userData = userDataString ? JSON.parse(userDataString) : null;
  // console.log(userData);

  //update user data with role and permission
  const userDataUpdate = [userData].map((user: any) => {
    const role = roleRoleDataArr.find((role: any) => role.id === user.role_id);
    const rolePermissions = rolePermissionDataArr.filter(
      (rp: any) => rp?.role_id === role?.id!,
    );
    const permissions = PermissionDataArr.filter((permission: any) =>
      rolePermissions.some((rp: any) => rp.permission_id === permission.id),
    );

    return {
      ...user,
      role: role ? role.name : null,
      permissions: [...permissions.map((p: any) => p.name)],
    };
  });

  //updated user to localstorage
  if (isRoleSuccess && isRolePermissionSuccess && isPermissionSuccess) {
    localStorage.setItem("user", JSON.stringify(userDataUpdate[0]) || "");
  }

  //fetch all student data
  const { data: studentsData, isLoading: isStudentLoading } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });

  //sclarité request
  useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });

  //fetch all class data
  useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });

  //fetch all user data
  useQuery({
    queryKey: ["users"],
    queryFn: userController.getAll,
  });
  //fetch all personnel data
  const { data: personnelData, isLoading: isPersonnelLoading } = useQuery({
    queryKey: ["personals"],
    queryFn: personalController.getAll,
  });

  //fetch all payment data
  const { data: paiementData, isLoading: isLoadingPayment } = useQuery({
    queryKey: ["payments"],
    queryFn: paymentController.getAll,
  });

  // fetch motif Paiement
  useQuery({
    queryKey: ["motifPaiement"],
    queryFn: motifPaiementController.getAll,
  });

  //fetch depense
  const { data: depenseData } = useQuery({
    queryKey: ["depenses"],
    queryFn: depenseController.getAll,
  });

  //fetch attendance of the current month
  const date = moment().format("YYYY-MM-DD");

  const { data: studentAttendance, isLoading: isStudentAttendanceLoading } =
    useQuery({
      queryKey: ["studentAttendance"],
      queryFn: () => studentAttendanceController.getAllByMonth(date),
    });

  //sclarité request
  useQuery({
    queryKey: ["parents"],
    queryFn: parentController.getAll,
  });
  const { data: promotionData } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });
  useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });
  const { data: schoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  // fetch Active schoolYear
  useQuery({
    queryKey: ["activeSchoolYears"],
    queryFn: schoolYearController.getActive,
  });

  const personalDataArray = personnelData?.data || [];
  const studentDataArray = studentsData?.data || [];
  const promotionArr = promotionData?.data || [];
  const schoolYearArr = schoolYear?.data || [];
  const selectedSchoolYear = schoolYearArr.find((sy: any) => sy.selected);

  // console.log(personalDataArray);
  //get connected user data from localstorage
  const currentUser = localStorage.getItem("user");
  const currentUserIParse = currentUser
    ? JSON.parse(currentUser).personnel_id
    : null;
  //get current user data from personnel data array
  const currentUserData = personalDataArray.find(
    (personal) => personal.personnel_id === currentUserIParse,
  );

  //add current user data to localstorage
  if (currentUserData) {
    localStorage.setItem(
      "user",
      JSON.stringify({
        ...userDataUpdate[0],
        userData: currentUserData,
      }) || "",
    );
  }

  //filter out active personnels
  const personnalDataActive = personalDataArray.filter(
    (personal) => personal.status,
  );

  //Create explicite data for givent school year for students
  let updatedStudents = studentDataArray.map((student) => {
    let promotionData: any;

    for (const promotion of promotionArr) {
      if (
        promotion.annee_scolaire_id === selectedSchoolYear?.id &&
        promotion.eleve_id === student.eleve_id
      ) {
        promotionData = promotion;
        //it is possible to get all promotion of the current student here by handling the condition above
      }
    }

    return {
      ...student,
      promotionData: promotionData,
    };
  });

  //filter out students without promotion in the selected school year
  updatedStudents = updatedStudents.filter((student) => student.promotionData);

  // const students = [...updatedStudents];
  // console.log(studentsData);
  // console.log(personnelData);
  // console.log(paiementData);
  // console.log(depenseData);
  // console.log(studentAttendance);

  //calculate average of presence by month
  // const currentMonth = moment().format("YYYY-MM");
  const monthlyData = studentAttendance?.data ? studentAttendance.data : [];
  // console.log(monthlyData);
  // const monthlyData = attendanceArray.filter((entry) =>
  //   entry.jour.startsWith(currentMonth)
  // );
  const total = monthlyData.length;
  const present = monthlyData.filter(
    (entry: PonctualiteType) =>
      entry.cote_eleve_id === 1 || entry.cote_eleve_id === 2,
  ).length;

  const presenceRate = total > 0 ? ((present / total) * 100).toFixed(3) : 0;

  //Calculate scool fees for the current month
  const paymentArray = paiementData?.data ? paiementData.data : [];
  const monthlyPaymentData = paymentArray.filter((entry) => {
    const byMonth = entry.created_at.startsWith(moment().format("YYYY-MM"));
    const byNotCanceled = entry.status;
    return byMonth && byNotCanceled;
  });
  const totalMontant = monthlyPaymentData.reduce(
    (sum, item) => sum + item.montant,
    0,
  );

  return (
    <div className="md:ml-60 w-full">
      {/* Header */}
      <Header
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        toggleDarkMode={toggleDarkMode}
        currentYear={currentYear}
        isYearDropdownOpen={isYearDropdownOpen}
        toggleYearDropdown={toggleYearDropdown}
        setCurrentYear={setCurrentYear}
        setIsYearDropdownOpen={setIsYearDropdownOpen}
      />

      {/* Dashboard Content */}
      <main className="ml-20 md:ml-0 p-6 mt-16">
        {isPersonnelLoading ? (
          <DashboardHeaderSkeleton isDarkMode={isDarkMode} />
        ) : (
          <div className="mb-8">
            <h1 className="text-md font-medium mb-1">Tableau de bord</h1>
            <p
              className={`${isDarkMode ? "text-gray-400" : "text-gray-600"} text-xs`}
            >
              Bienvenue sur votre tableau de bord de gestion scolaire.
            </p>
          </div>
        )}
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {/* ==================== ÉLÈVES ==================== */}
          {isStudentLoading ? (
            <StatCardSkeleton isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`
        relative min-h-[138px] rounded-lg border px-5 py-4
        overflow-hidden transition-all duration-200
        ${
          isDarkMode
            ? "bg-[#161B22] border-[#30363D] hover:border-[#484F58]"
            : "bg-white border-gray-200 shadow-sm hover:shadow-md"
        }
      `}
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <h2 className="font-semibold text-[13px]">Total Élèves</h2>

                <div
                  className={`
            w-9 h-9 rounded-full border
            flex items-center justify-center
            ${
              isDarkMode
                ? "bg-blue-500/10 border-blue-500/30"
                : "bg-blue-50 border-blue-200"
            }
          `}
                >
                  <i className="fas fa-user-graduate text-blue-500 text-sm" />
                </div>
              </div>

              {/* Bottom */}
              <div className="absolute left-5 right-4 bottom-4 flex items-end justify-between">
                <div>
                  <div className="text-[26px] leading-none font-bold">
                    {updatedStudents.length}
                  </div>

                  <div
                    className={`text-[11px] mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Élèves inscrits
                  </div>
                </div>

                <MiniChart
                  color={isDarkMode ? "#9CA3AF" : "#64748B"}
                  variant={1}
                />
              </div>
            </div>
          )}

          {/* ==================== PERSONNELS ==================== */}
          {isPersonnelLoading ? (
            <StatCardSkeleton isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`
        relative min-h-[138px] rounded-lg border px-5 py-4
        overflow-hidden transition-all duration-200
        ${
          isDarkMode
            ? "bg-[#161B22] border-[#30363D] hover:border-[#484F58]"
            : "bg-white border-gray-200 shadow-sm hover:shadow-md"
        }
      `}
            >
              <div className="flex items-start justify-between">
                <h2 className="font-semibold text-[13px]">Personnels</h2>

                <div
                  className={`
            w-9 h-9 rounded-full border
            flex items-center justify-center
            ${
              isDarkMode
                ? "bg-violet-500/10 border-violet-500/30"
                : "bg-violet-50 border-violet-200"
            }
          `}
                >
                  <i className="fas fa-chalkboard-teacher text-violet-500 text-sm" />
                </div>
              </div>

              <div className="absolute left-5 right-4 bottom-4 flex items-end justify-between">
                <div>
                  <div className="text-[26px] leading-none font-bold">
                    {personnalDataActive.length}
                  </div>

                  <div
                    className={`text-[11px] mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Personnels actifs
                  </div>
                </div>

                <MiniChart
                  color={isDarkMode ? "#9CA3AF" : "#64748B"}
                  variant={2}
                />
              </div>
            </div>
          )}

          {/* ==================== PRÉSENCE ==================== */}
          {isStudentAttendanceLoading ? (
            <StatCardSkeleton isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`
        relative min-h-[138px] rounded-lg border px-5 py-4
        overflow-hidden transition-all duration-200
        ${
          isDarkMode
            ? "bg-[#161B22] border-[#30363D] hover:border-[#484F58]"
            : "bg-white border-gray-200 shadow-sm hover:shadow-md"
        }
      `}
            >
              <div className="flex items-start justify-between">
                <h2 className="font-semibold text-[13px]">Taux de présence</h2>

                <div
                  className={`
            w-9 h-9 rounded-full border
            flex items-center justify-center
            ${
              isDarkMode
                ? "bg-emerald-500/10 border-emerald-500/30"
                : "bg-emerald-50 border-emerald-200"
            }
          `}
                >
                  <i className="fas fa-clipboard-check text-emerald-500 text-sm" />
                </div>
              </div>

              <div className="absolute left-5 right-4 bottom-4 flex items-end justify-between">
                <div>
                  <div className="text-[26px] leading-none font-bold whitespace-nowrap">
                    {presenceRate}%
                  </div>

                  <div
                    className={`text-[11px] mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Moyenne mensuelle
                  </div>
                </div>

                <MiniChart
                  color={isDarkMode ? "#6EE7B7" : "#10B981"}
                  variant={3}
                />
              </div>
            </div>
          )}

          {/* ==================== PAIEMENTS ==================== */}
          {isLoadingPayment ? (
            <StatCardSkeleton isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`
        relative min-h-[138px] rounded-lg border px-5 py-4
        overflow-hidden transition-all duration-200
        ${
          isDarkMode
            ? "bg-[#161B22] border-[#30363D] hover:border-[#484F58]"
            : "bg-white border-gray-200 shadow-sm hover:shadow-md"
        }
      `}
            >
              <div className="flex items-start justify-between">
                <h2 className="font-semibold text-[13px]">Paiements</h2>

                <div
                  className={`
            w-9 h-9 rounded-full border
            flex items-center justify-center
            ${
              isDarkMode
                ? "bg-purple-500/10 border-purple-500/30"
                : "bg-purple-50 border-purple-200"
            }
          `}
                >
                  <i className="fas fa-credit-card text-purple-500 text-sm" />
                </div>
              </div>

              <div className="absolute left-5 right-4 bottom-4 flex items-end justify-between">
                <div>
                  <div className="text-[26px] leading-none font-bold whitespace-nowrap">
                    {totalMontant.toFixed(2)} $
                  </div>

                  <div
                    className={`text-[11px] mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Frais perçus ce mois
                  </div>
                </div>

                <MiniChart
                  color={isDarkMode ? "#C084FC" : "#A855F7"}
                  variant={4}
                />
              </div>
            </div>
          )}
        </div>

        {/* charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* paiements */}

          {isLoadingPayment ? (
            <PaymentStatsSkeleton isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`rounded-lg p-6 ${
                isDarkMode ? "bg-[#161B22]" : "bg-white"
              } ${!isDarkMode && "shadow-md"} lg:col-span-2`}
            >
              <h2 className="text-sm sm:text-md font-bold mb-6">
                Statistiques annuelles des paiements
              </h2>
              <div className="space-y-6">
                {/* <paiementContext.Provider value={paiementData}> */}
                <depenseContext.Provider value={depenseData}>
                  <ColumnChart />
                </depenseContext.Provider>
                {/* </paiementContext.Provider> */}
              </div>
            </div>
          )}

          {/* stats gender */}

          {isStudentLoading ? (
            <StudentStatsSkeleton isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`rounded-lg p-6 ${
                isDarkMode ? "bg-[#161B22]" : "bg-white"
              } ${!isDarkMode && "shadow-md"}`}
            >
              <h2 className="text-sm sm:text-md font-bold mb-6">
                Statistiques - Elèves{" "}
              </h2>
              <div
                className={`rounded-lg p-4 mb-4 ${
                  isDarkMode ? "bg-[#0D1117]" : "bg-gray-50"
                }`}
              >
                <h3 className="text-xs lg:text-sm lg:font-medium mb-2">
                  Par genre
                </h3>

                <CircularChartPaimentPerClass
                  data={updatedStudents}
                  isLoading={isStudentLoading}
                />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
